// Nueva cotización: se captura aquí y se CREA EN SAP (fuente única de verdad).
// Hechos de SAP que dictan la UI: 50k artículos → búsqueda, no desplegable; listas de precios vacías →
// el precio se teclea y solo se sugiere el último cotizado; 17% de líneas son libres (GENERICO).
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input, Select } from '@/components/ui/Field'
import { formatMoney } from '@/services/deliveries'
import { useAuth } from '@/hooks/useAuth'
import {
  createQuote, getItemStock, getLastPrice, getQuoteMeta, searchCustomers, searchItems,
  type CustomerHit, type LastPrice, type Quote, type QuoteCurrency, type QuoteLineIn, type StockRow, type TaxCode,
} from '@/services/quotes'

interface Line extends QuoteLineIn {
  key: number
  unit?: string | null
  last?: LastPrice | null   // undefined = cargando
  stock?: StockRow[]
}

function useDebounced(value: string, ms = 350) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

const plusDays = (n: number) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10)
const cell = 'w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-ds-500 focus:outline-none focus:ring-2 focus:ring-ds-500/20'
let nextKey = 1

export default function QuoteNew() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const meta = useQuery({ queryKey: ['quotes', 'meta'], queryFn: getQuoteMeta, staleTime: 10 * 60_000 })

  const [customer, setCustomer] = useState<CustomerHit | null>(null)
  const [warehouse, setWarehouse] = useState('')
  const [currency, setCurrency] = useState<QuoteCurrency>('MXP')
  const [dueDate, setDueDate] = useState(plusDays(15))
  const [comments, setComments] = useState('')
  const [lines, setLines] = useState<Line[]>([])
  const [error, setError] = useState<string | null>(null)

  // Almacén por omisión del usuario (o el primero con serie).
  useEffect(() => {
    if (meta.data && !warehouse) setWarehouse(meta.data.default_warehouse ?? meta.data.warehouses[0] ?? '')
  }, [meta.data, warehouse])

  // Búsquedas (server-side, con debounce).
  const [custQ, setCustQ] = useState('')
  const [itemQ, setItemQ] = useState('')
  const custQd = useDebounced(custQ)
  const itemQd = useDebounced(itemQ)
  const custHits = useQuery({ queryKey: ['quotes', 'customers', custQd], queryFn: () => searchCustomers(custQd), enabled: custQd.trim().length >= 2 })
  const itemHits = useQuery({ queryKey: ['quotes', 'items', itemQd], queryFn: () => searchItems(itemQd), enabled: itemQd.trim().length >= 2 })

  const rateOf = (t: TaxCode) => meta.data?.tax_codes.find((x) => x.code === t)?.rate ?? 0.16
  const totals = useMemo(() => {
    const sub = lines.reduce((a, l) => a + l.quantity * l.unit_price, 0)
    const iva = lines.reduce((a, l) => a + l.quantity * l.unit_price * rateOf(l.tax_code), 0)
    return { sub, iva, total: sub + iva }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, meta.data])

  const patch = (key: number, ch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...ch } : l)))

  async function addItem(code: string, name: string, unit: string | null) {
    const key = nextKey++
    setLines((ls) => [...ls, { key, item_code: code, description: name, quantity: 1, unit_price: 0, tax_code: '16V', unit, last: undefined }])
    setItemQ('')
    const [last, stock] = await Promise.all([
      getLastPrice(code, customer?.code).catch(() => null),
      getItemStock(code).catch(() => [] as StockRow[]),
    ])
    // Sugerir el último precio solo si el vendedor no ha tecleado uno todavía.
    setLines((ls) => ls.map((l) => (l.key === key
      ? { ...l, last, stock, unit_price: l.unit_price === 0 && last && last.currency === currency ? last.price : l.unit_price }
      : l)))
  }
  const addFree = () => setLines((ls) => [...ls, { key: nextKey++, item_code: null, description: '', quantity: 1, unit_price: 0, tax_code: '16V', last: null, stock: [] }])

  const create = useMutation({
    mutationFn: () => createQuote({
      card_code: customer!.code, warehouse, currency, due_date: dueDate, comments: comments || null,
      lines: lines.map(({ item_code, description, quantity, unit_price, tax_code }) => ({ item_code, description: description.trim(), quantity, unit_price, tax_code })),
    }),
    onSuccess: (q) => { qc.invalidateQueries({ queryKey: ['quotes'] }); setError(null); setDone(q) },
    onError: (e: unknown) => setError((e as { message?: string })?.message ?? 'No se pudo crear la cotización.'),
  })
  const [done, setDone] = useState<Quote | null>(null)

  const canSubmit = !!customer && !!warehouse && lines.length > 0 && lines.every((l) => l.description.trim() && l.quantity > 0 && l.unit_price >= 0) && !create.isPending
  const noSeller = meta.data && !meta.data.sales_person

  if (done) {
    return (
      <div className="mx-auto max-w-lg">
        <Card className="p-6 text-center">
          <div className="text-4xl">✅</div>
          <h1 className="mt-2 text-xl font-bold text-gray-900">Cotización {done.DocNum} creada en SAP</h1>
          <p className="mt-1 text-sm text-gray-500">{customer?.name} · {formatMoney(totals.total, currency)}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <a href={done.pdf_url} target="_blank" rel="noopener"><Button>Abrir PDF</Button></a>
            <Button variant="secondary" onClick={() => { setDone(null); setLines([]); setCustomer(null); setComments('') }}>Otra cotización</Button>
            <Button variant="ghost" onClick={() => nav('/cotizaciones')}>Ver mis cotizaciones</Button>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="Nueva cotización" subtitle="Se registra directamente en SAP con tu folio de vendedor."
        actions={<Link to="/cotizaciones" className="text-sm text-gray-500 hover:underline">← Mis cotizaciones</Link>} />

      {noSeller && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {user?.role === 'admin'
            ? <>Esta cotización se registrará <b>sin vendedor asignado</b> en SAP, porque tu correo ({user.email}) no corresponde a ningún vendedor del catálogo de SAP. Para que salga a nombre de un vendedor, entra con la cuenta de ese vendedor.</>
            : <>No puedes cotizar todavía: tu correo ({user?.email}) no corresponde a ningún vendedor del catálogo de SAP. Pide a administración que lo den de alta en SAP con ese mismo correo.</>}
        </div>
      )}

      {/* Cabecera */}
      <Card className="mb-4 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
          <div className="relative sm:col-span-3">
            <Input label="Cliente" placeholder="Nombre o código (mín. 2 letras)" value={customer ? `${customer.name} (${customer.code})` : custQ}
              onChange={(e) => { setCustomer(null); setCustQ(e.target.value) }} autoComplete="off" />
            {!customer && custQd.trim().length >= 2 && (
              <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {custHits.isLoading && <li className="p-2 text-sm text-gray-400">Buscando en SAP…</li>}
                {custHits.data?.map((c) => (
                  <li key={c.code}>
                    <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-ds-50"
                      onClick={() => { setCustomer(c); setCustQ(''); if (c.currency === 'USD' || c.currency === 'MXP' || c.currency === 'EUR') setCurrency(c.currency) }}>
                      <span className="font-medium">{c.name}</span> <span className="text-gray-400">· {c.code}{c.rfc ? ` · ${c.rfc}` : ''}{c.currency ? ` · ${c.currency}` : ''}</span>
                    </button>
                  </li>
                ))}
                {custHits.data?.length === 0 && <li className="p-2 text-sm text-gray-400">Sin resultados.</li>}
              </ul>
            )}
          </div>
          <Select label="Almacén / serie" value={warehouse} onChange={(e) => setWarehouse(e.target.value)}>
            {meta.data?.warehouses.map((w) => <option key={w} value={w}>{w}</option>)}
          </Select>
          <Select label="Moneda" value={currency} onChange={(e) => setCurrency(e.target.value as QuoteCurrency)}>
            <option value="MXP">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
          </Select>
          <Input label="Vigencia" type="date" value={dueDate} min={plusDays(0)} onChange={(e) => setDueDate(e.target.value)} />
          <div className="sm:col-span-6">
            <Input label="Observaciones (opcional)" maxLength={254} value={comments} onChange={(e) => setComments(e.target.value)} />
          </div>
        </div>
      </Card>

      {/* Partidas */}
      <Card className="mb-4 p-4">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="relative min-w-0 flex-1">
            <Input label="Agregar artículo del catálogo" placeholder="Descripción o código (mín. 2 letras)" value={itemQ} onChange={(e) => setItemQ(e.target.value)} autoComplete="off" />
            {itemQd.trim().length >= 2 && itemQ && (
              <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                {itemHits.isLoading && <li className="p-2 text-sm text-gray-400">Buscando en SAP…</li>}
                {itemHits.data?.map((i) => (
                  <li key={i.code}>
                    <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-ds-50" onClick={() => addItem(i.code, i.name, i.unit)}>
                      <span className="font-mono text-xs text-gray-500">{i.code}</span> <span>{i.name}</span>
                    </button>
                  </li>
                ))}
                {itemHits.data?.length === 0 && <li className="p-2 text-sm text-gray-400">Sin resultados.</li>}
              </ul>
            )}
          </div>
          <Button type="button" variant="secondary" onClick={addFree}>＋ Línea libre</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-2">Código</th><th className="p-2">Descripción</th>
                <th className="p-2 w-24 text-right">Cant.</th><th className="p-2 w-32 text-right">P. unitario</th>
                <th className="p-2 w-28">IVA</th><th className="p-2 w-32 text-right">Importe</th><th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => (
                <tr key={l.key} className="align-top">
                  <td className="p-2 font-mono text-xs text-gray-600">{l.item_code ?? <span className="text-gray-400">libre</span>}</td>
                  <td className="p-2">
                    <input className={cell} value={l.description} maxLength={254} placeholder="Descripción" onChange={(e) => patch(l.key, { description: e.target.value })} />
                    {l.item_code && (
                      <div className="mt-1 text-xs text-gray-500">
                        {l.last === undefined ? 'Consultando último precio y existencias…' : (
                          <>
                            {l.last
                              ? <>Último: <b>{formatMoney(l.last.price, l.last.currency)}</b> {l.last.same_customer ? 'a este cliente' : `a ${l.last.card_name}`} ({l.last.date}, cot. {l.last.doc_num})</>
                              : 'Sin precio previo cotizado'}
                            {' · '}
                            {l.stock && l.stock.length > 0
                              ? <>Disp.: {l.stock.map((s) => `${s.warehouse} ${s.available}`).join(' · ')}</>
                              : 'Sin existencias'}
                            {l.unit ? ` · ${l.unit}` : ''}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-2"><input className={`${cell} text-right`} type="number" min={0.01} step="any" value={l.quantity} onChange={(e) => patch(l.key, { quantity: Number(e.target.value) })} /></td>
                  <td className="p-2"><input className={`${cell} text-right`} type="number" min={0} step="0.01" value={l.unit_price} onChange={(e) => patch(l.key, { unit_price: Number(e.target.value) })} /></td>
                  <td className="p-2">
                    <select className={cell} value={l.tax_code} onChange={(e) => patch(l.key, { tax_code: e.target.value as TaxCode })}>
                      {meta.data?.tax_codes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select>
                  </td>
                  <td className="p-2 text-right tabular-nums">{formatMoney(l.quantity * l.unit_price, currency)}</td>
                  <td className="p-2"><button type="button" className="text-gray-400 hover:text-red-600" title="Quitar" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>✕</button></td>
                </tr>
              ))}
              {lines.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-gray-400">Busca un artículo o agrega una línea libre.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="mt-3 ml-auto w-full max-w-xs text-sm">
          <div className="flex justify-between py-0.5 text-gray-600"><span>Subtotal</span><span className="tabular-nums">{formatMoney(totals.sub, currency)}</span></div>
          <div className="flex justify-between py-0.5 text-gray-600"><span>IVA</span><span className="tabular-nums">{formatMoney(totals.iva, currency)}</span></div>
          <div className="flex justify-between border-t border-gray-200 pt-1 text-base font-bold text-gray-900"><span>Total</span><span className="tabular-nums">{formatMoney(totals.total, currency)}</span></div>
        </div>
      </Card>

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => nav('/cotizaciones')}>Cancelar</Button>
        <Button disabled={!canSubmit} onClick={() => {
          if (window.confirm(`Se creará la cotización en SAP para ${customer?.name} por ${formatMoney(totals.total, currency)}. ¿Continuar?`)) create.mutate()
        }}>
          {create.isPending ? 'Creando en SAP…' : 'Crear cotización'}
        </Button>
      </div>
    </div>
  )
}
